/* @flow */
import * as React from 'react';
import { once } from 'lodash';

import AdminSisSyncHoldViewResourceTable from './AdminSisSyncHoldViewResourceTable';
import AdminSisSyncHoldViewResumeModal from './AdminSisSyncHoldViewResumeModal';
import AdminSchoolSisSyncHeader from 'admin/components/AdminSchoolSisSyncHeader';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Copy from 'components/Copy';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import ModalTrigger from 'components/ModalTrigger';
import SyncHoldCopy from 'admin/constants/SyncHoldCopy';
import Text from 'components/Text';

import type { HoldSummary } from 'admin/components/AdminSchoolSisDashboard';

import './styles/AdminSisSyncHoldView.scss';

type Props = {
  orgUuid: string,
  holdSummary: HoldSummary,
  lastSyncTimestamp: number,
  syncMethod: string,
};

export default class AdminSisSyncHoldView extends React.Component<Props> {
  componentWillMount() {
    this.trackView();
  }

  renderHeader() {
    const subtext =
      'The most recent sync has been placed on hold. Review the following to resolve the issue and continue syncing.';

    return (
      <AdminSchoolSisSyncHeader
        subtext={subtext}
        status="on hold"
        lastSyncTimestamp={this.props.lastSyncTimestamp}
      />
    );
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.hold.viewed', {
      organization_uud: this.props.orgUuid,
    });
  });

  render() {
    const {
      orgUuid,
      syncMethod,
      holdSummary: { hold, temporarilyIgnoreable, permanentlyIgnoreable, affectedResourcesSummary },
    } = this.props;
    const { reason, nextStep } = SyncHoldCopy[hold];

    return (
      <section className="AdminSisSyncHoldView">
        {this.renderHeader()}
        <Box textAlign="left">
          <BoxBody full className="AdminSisSyncHoldView-reason">
            <Heading level="2" levelStyle="3">
              <Text color="white">Hold Reason</Text>
            </Heading>
            <Copy>
              <Text color="white" size="medium">
                <Text element="span" weight="semibold" size="medium">
                  {reason.problem}
                </Text>{' '}
                {reason.explanation}
              </Text>
            </Copy>
          </BoxBody>
          <BoxBody divided className="AdminSisSyncHoldView-next">
            <Heading level="2" levelStyle="3">
              Next Step
            </Heading>
            <Copy>
              <Text size="medium">
                <p>{nextStep.intro}</p>
                {nextStep.bullets && (
                  <ul>
                    {nextStep.bullets.map(text => {
                      return (
                        <li>
                          <p>{text}</p>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {nextStep.footer && <p>{nextStep.footer}</p>}
              </Text>
            </Copy>
          </BoxBody>
          {affectedResourcesSummary.length !== 0 && (
            <AdminSisSyncHoldViewResourceTable
              orgUuid={orgUuid}
              affectedResources={affectedResourcesSummary}
            />
          )}
          <BoxFooter className="AdminSisSyncHoldView-footer">
            <Buttons align="left">
              <ModalTrigger
                modal={
                  <AdminSisSyncHoldViewResumeModal
                    holdKey={hold}
                    orgUuid={orgUuid}
                    syncMethod={syncMethod}
                  />
                }>
                <Button>I fixed it</Button>
              </ModalTrigger>
              {temporarilyIgnoreable && (
                <ModalTrigger
                  modal={
                    <AdminSisSyncHoldViewResumeModal
                      holdKey={hold}
                      orgUuid={orgUuid}
                      permanentlyIgnoreable={permanentlyIgnoreable}
                      temporarilyIgnored
                    />
                  }>
                  <Button level="secondary">Ignore the hold and continue</Button>
                </ModalTrigger>
              )}
            </Buttons>
          </BoxFooter>
        </Box>
      </section>
    );
  }
}
